<!--
 * 银川智慧城市 · 大屏演示
-->
<template>
  <div class="app">
    <div id="cesiumContainer" ref="cesiumContainer"></div>

    <!-- 顶栏 -->
    <header class="top-bar">
      <div class="top-bar__side top-bar__side--left">
        <span class="top-bar__org">{{ DEMO.org }}</span>
      </div>
      <div class="top-bar__brand">
        <h1>{{ DEMO.title }}</h1>
        <p>{{ DEMO.subtitle }}</p>
      </div>
      <div class="top-bar__side top-bar__side--right">
        <time class="top-bar__clock">{{ clockText }}</time>
      </div>
    </header>

    <!-- 加载遮罩 -->
    <div class="loader" v-if="loading">
      <div class="loader__card">
        <div class="loader__title">系统初始化</div>
        <div class="loader__bar">
          <i :style="{ width: loadPercent + '%' }"></i>
        </div>
        <div class="loader__msg">{{ loadMessage }}</div>
      </div>
    </div>

    <!-- 左侧：剧本 + 导览 -->
    <aside class="left-panel">
      <section class="panel-block">
        <header class="panel-block__head">
          <h3>演示模式</h3>
          <span class="script-meta">约 {{ scriptDuration }}s</span>
        </header>
        <div class="script-controls">
          <button
            class="tour-btn"
            :class="{ 'tour-btn--on': scriptPlaying && !scriptPaused }"
            :disabled="loading"
            @click="toggleScript"
          >
            {{ scriptBtnLabel }}
          </button>
          <button
            class="tour-btn tour-btn--ghost"
            :disabled="!scriptPlaying"
            @click="stopScript"
          >
            停止
          </button>
        </div>
        <div class="script-progress" v-if="scriptPlaying || scriptStepIndex >= 0">
          <div class="script-progress__bar">
            <i :style="{ width: scriptProgress + '%' }"></i>
          </div>
          <div class="script-progress__text">
            {{ scriptStepIndex + 1 }} / {{ DEMO_SCRIPT.steps.length }}
            · {{ currentStepTitle }}
          </div>
        </div>
        <ul class="script-steps">
          <li
            v-for="(step, i) in DEMO_SCRIPT.steps"
            :key="step.id"
            class="script-step"
            :class="{
              'script-step--active': scriptStepIndex === i,
              'script-step--done': scriptStepIndex > i,
            }"
            @click="jumpToScriptStep(i)"
          >
            <span class="script-step__idx">{{ i + 1 }}</span>
            <span class="script-step__title">{{ step.title }}</span>
          </li>
        </ul>
      </section>

      <section class="panel-block">
        <header class="panel-block__head">
          <h3>场景导览</h3>
          <button
            class="tour-btn"
            :class="{ 'tour-btn--on': autoTour }"
            :disabled="scriptPlaying"
            @click="toggleAutoTour"
          >
            {{ autoTour ? "停止巡游" : "自动巡游" }}
          </button>
        </header>
        <ul class="tour-list">
          <li
            v-for="spot in TOUR_SPOTS"
            :key="spot.key"
            class="tour-item"
            :class="{ 'tour-item--active': activeSpot === spot.key }"
            @click="flyToSpot(spot)"
          >
            <span class="tour-item__name">{{ spot.name }}</span>
          </li>
        </ul>
        <div class="cam-save">
          <button class="tour-btn" :disabled="loading" @click="saveCameraView">
            保存相机视角
          </button>
          <button
            class="tour-btn tour-btn--ghost"
            :disabled="!savedCamera"
            @click="copySavedCamera"
          >
            复制
          </button>
        </div>
        <pre v-if="savedCamera" class="cam-save__out">{{ savedCameraText }}</pre>
      </section>

      <section class="panel-block panel-block--alerts">
        <header class="panel-block__head">
          <h3>告警演示</h3>
          <button
            class="tour-btn"
            :class="{ 'tour-btn--on': alertVisible }"
            :disabled="loading"
            @click="toggleAlerts"
          >
            {{ alertVisible ? "隐藏" : "显示" }}
          </button>
        </header>
        <div class="alert-stats">
          <div class="alert-stat">
            <em>{{ alertSummary.total }}</em>
            <span>全部</span>
          </div>
          <div class="alert-stat alert-stat--critical">
            <em>{{ alertSummary.critical }}</em>
            <span>紧急</span>
          </div>
          <div class="alert-stat alert-stat--warning">
            <em>{{ alertSummary.warning }}</em>
            <span>重要</span>
          </div>
          <div class="alert-stat alert-stat--info">
            <em>{{ alertSummary.info }}</em>
            <span>一般</span>
          </div>
        </div>
        <ul class="alert-list">
          <li
            v-for="a in DEMO_ALERTS"
            :key="a.id"
            class="alert-item"
            :class="[
              'alert-item--' + a.level,
              { 'alert-item--active': activeAlertId === a.id },
            ]"
            @click="focusAlert(a)"
          >
            <span class="alert-item__level">{{ ALERT_LEVELS[a.level].name }}</span>
            <div class="alert-item__body">
              <div class="alert-item__title">{{ a.title }}</div>
              <div class="alert-item__meta">{{ a.place }} · {{ a.time }}</div>
            </div>
          </li>
        </ul>
      </section>

      <section class="panel-block panel-block--pick" v-if="picked">
        <header class="panel-block__head">
          <h3>要素信息</h3>
          <button class="tour-btn tour-btn--ghost" @click="picked = null">关闭</button>
        </header>
        <dl class="pick-info">
          <div v-for="(v, k) in picked" :key="k" class="pick-info__row">
            <dt>{{ k }}</dt>
            <dd>{{ v }}</dd>
          </div>
        </dl>
      </section>
    </aside>

    <!-- 右侧：概览 + 图层 + 特效 -->
    <aside class="layer-panel">
      <section class="panel-block panel-block--kpi">
        <header class="panel-block__head">
          <h3>城市概览</h3>
        </header>
        <div class="kpi-grid">
          <div v-for="k in DEMO_KPIS" :key="k.key" class="kpi">
            <div class="kpi__label">{{ k.label }}</div>
            <div class="kpi__value">
              <em>{{ k.value }}</em><span>{{ k.unit }}</span>
            </div>
          </div>
        </div>
      </section>

      <header class="layer-panel__header">
        <h3>数据图层</h3>
        <span class="layer-panel__count">{{ layers.length }}</span>
      </header>
      <ul class="layer-panel__list" v-if="layers.length">
        <li v-for="layer in layers" :key="layer.key" class="layer-panel__item">
          <span
            class="layer-panel__dot"
            :style="{ background: layer.colorHex }"
          ></span>
          <span class="layer-panel__name">{{ layer.name }}</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="layer.visible"
              @change="toggleLayer(layer)"
            />
            <span class="switch__slider"></span>
          </label>
        </li>
      </ul>
      <p v-else class="layer-panel__empty">图层加载中…</p>

      <header class="layer-panel__header layer-panel__header--sub">
        <h3>特效</h3>
        <span class="layer-panel__count">{{ effects.length }}</span>
      </header>
      <ul class="layer-panel__list" v-if="effects.length">
        <li v-for="fx in effects" :key="fx.key" class="layer-panel__item">
          <span
            class="layer-panel__dot"
            :style="{ background: fx.colorHex }"
          ></span>
          <span class="layer-panel__name">{{ fx.name }}</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="fx.visible"
              @change="toggleEffect(fx)"
            />
            <span class="switch__slider"></span>
          </label>
        </li>
      </ul>
    </aside>

    <!-- 底部：字幕在上，图例在下，避免重叠 -->
    <div class="bottom-stack">
      <div class="caption" v-if="scriptCaption">
        <div class="caption__eyebrow">DEMO SCRIPT</div>
        <div class="caption__title">{{ currentStepTitle }}</div>
        <div class="caption__text">{{ scriptCaption }}</div>
      </div>
      <footer class="legend-bar" v-if="layers.length">
        <span class="legend-bar__label">图例</span>
        <span
          v-for="layer in layers"
          :key="'lg-' + layer.key"
          class="legend-bar__item"
          :class="{ 'legend-bar__item--off': !layer.visible }"
        >
          <i :style="{ background: layer.colorHex }"></i>
          {{ layer.name }}
        </span>
      </footer>
    </div>
  </div>
</template>

<script setup>
import * as Cesium from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { loadBaseLayers } from "@/cesium/loadBaseData";
import modifyMap from "@/cesium/modifyMap";
import LightCone from "@/cesium/LightCone";
import RectFlyLight from "@/cesium/RectFlyLight";
import RoadLightLine from "@/cesium/RoadLightLine";
import RadarLight from "@/cesium/RadarLight";
import LightSpread from "@/cesium/LightSpread";
import LightWall from "@/cesium/LightWall";
import ParticleLight from "@/cesium/ParticleLight";
import SceneLabel from "@/cesium/SceneLabel";
import AlertLayer, {
  DEMO_ALERTS,
  ALERT_LEVELS,
  summarizeAlerts,
} from "@/cesium/alertDemo";
import { CENTER } from "@/cesium/effectConfig";
import { sampleGroundHeight } from "@/cesium/groundHeight";
import { DEMO, DEMO_KPIS, TOUR_SPOTS, CAMERA_DEFAULT } from "@/cesium/demoConfig";
import { FOCUS_CORE } from "@/utils/point";
import {
  DEMO_SCRIPT,
  estimateScriptDuration,
  getSpotByKey,
} from "@/cesium/demoScript";

const layers = ref([]);
const effects = ref([]);
const loading = ref(true);
const loadPercent = ref(5);
const loadMessage = ref("创建三维场景…");
const clockText = ref("");
const activeSpot = ref("core");
const autoTour = ref(false);
const picked = ref(null);
const savedCamera = ref(null);
const alertVisible = ref(true);
const activeAlertId = ref(null);
const alertSummary = summarizeAlerts(DEMO_ALERTS);

const savedCameraText = computed(() => {
  const c = savedCamera.value;
  if (!c) return "";
  return [
    `lng: ${c.lng},`,
    `lat: ${c.lat},`,
    `height: ${c.height},  // 相对地表 ${c.heightAbs}m(绝对)`,
    `heading: ${c.heading},`,
    `pitch: ${c.pitch},`,
  ].join("\n");
});

const scriptPlaying = ref(false);
const scriptPaused = ref(false);
const scriptStepIndex = ref(-1);
const scriptCaption = ref("");
const scriptProgress = ref(0);
const scriptDuration = estimateScriptDuration();

const currentStepTitle = computed(() => {
  const step = DEMO_SCRIPT.steps[scriptStepIndex.value];
  return step?.title ?? "待命";
});

const scriptBtnLabel = computed(() => {
  if (!scriptPlaying.value) return "播放剧本";
  if (scriptPaused.value) return "继续";
  return "暂停";
});

let viewerRef = null;
let groundHeightRef = 1110;
let sceneLabelRef = null;
let alertLayerRef = null;
let clockTimer = null;
let tourTimer = null;
let tourIndex = 0;
let pickHandler = null;
let scriptTimer = null;
let scriptHoldTimer = null;
let scriptProgressTimer = null;
let scriptAbort = false;
let scriptGen = 0;

function setLoad(pct, msg) {
  loadPercent.value = pct;
  loadMessage.value = msg;
}

function setLayerVisible(layer, visible) {
  layer.visible = visible;
  if (layer.dataSource) layer.dataSource.show = visible;
}

function setEffectVisible(fx, visible) {
  fx.visible = visible;
  fx.instance?.setVisible?.(visible);
}

function toggleLayer(layer) {
  setLayerVisible(layer, !layer.visible);
}

function toggleEffect(fx) {
  setEffectVisible(fx, !fx.visible);
}

function toggleAlerts() {
  alertVisible.value = !alertVisible.value;
  alertLayerRef?.setVisible(alertVisible.value);
  if (!alertVisible.value) activeAlertId.value = null;
}

function focusAlert(alert) {
  if (!alertLayerRef || loading.value) return;
  if (!alertVisible.value) {
    alertVisible.value = true;
    alertLayerRef.setVisible(true);
  }
  activeAlertId.value = alert.id;
  activeSpot.value = "";
  alertLayerRef.flyTo(alert.id);
}

function applyLayersConfig(step) {
  if (step.layersOnly) {
    const allow = new Set(step.layersOnly);
    layers.value.forEach((l) => setLayerVisible(l, allow.has(l.key)));
    return;
  }
  if (step.layers) {
    layers.value.forEach((l) => {
      if (Object.prototype.hasOwnProperty.call(step.layers, l.key)) {
        setLayerVisible(l, !!step.layers[l.key]);
      }
    });
  }
}

function applyEffectsConfig(step) {
  if (step.effectsOnly) {
    const allow = new Set(step.effectsOnly);
    // 道路飞线任意场景都保持开启
    allow.add("roadLine");
    effects.value.forEach((fx) => setEffectVisible(fx, allow.has(fx.key)));
  } else if (step.effects) {
    effects.value.forEach((fx) => {
      if (Object.prototype.hasOwnProperty.call(step.effects, fx.key)) {
        setEffectVisible(fx, !!step.effects[fx.key]);
      }
    });
  }
  const roadLine = effects.value.find((fx) => fx.key === "roadLine");
  if (roadLine) setEffectVisible(roadLine, true);
}

function updateClock() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  clockText.value = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}  ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function flyToSpot(spot, duration, labelName) {
  if (!viewerRef || !spot) return;
  activeSpot.value = spot.key;
  const h = groundHeightRef + (spot.height ?? 1800);
  viewerRef.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(spot.lng, spot.lat, h),
    orientation: {
      heading: Cesium.Math.toRadians(spot.heading ?? 0),
      pitch: Cesium.Math.toRadians(spot.pitch ?? -30),
      roll: 0,
    },
    duration: duration ?? spot.duration ?? 2.2,
  });

  showSceneLabelForSpot(spot, labelName);
}

/** 每个场景切换必显示名称标签 */
function showSceneLabelForSpot(spot, labelName) {
  if (!sceneLabelRef || !spot) return;
  const name = labelName || spot.name || spot.key;
  const lng = spot.labelLng ?? spot.lng;
  const lat = spot.labelLat ?? spot.lat;
  const lift =
    spot.labelLift ??
    Math.min(Math.max((spot.height ?? 2000) * 0.06, 100), 2500);
  sceneLabelRef.show(name, lng, lat, lift);
}

/** 保存当前相机视角（可复制进 demoConfig） */
function saveCameraView() {
  if (!viewerRef) return;
  const cam = viewerRef.camera;
  const carto = Cesium.Cartographic.fromCartesian(cam.positionWC);
  const lng = Cesium.Math.toDegrees(carto.longitude);
  const lat = Cesium.Math.toDegrees(carto.latitude);
  const heightAbs = carto.height;
  const heightRel = heightAbs - groundHeightRef;
  const heading = Cesium.Math.toDegrees(cam.heading);
  const pitch = Cesium.Math.toDegrees(cam.pitch);
  const roll = Cesium.Math.toDegrees(cam.roll);

  savedCamera.value = {
    lng: Number(lng.toFixed(6)),
    lat: Number(lat.toFixed(6)),
    height: Math.round(heightRel),
    heightAbs: Math.round(heightAbs),
    heading: Number(heading.toFixed(1)),
    pitch: Number(pitch.toFixed(1)),
    roll: Number(roll.toFixed(1)),
  };

  console.log("[相机视角]", savedCamera.value);
  console.log("[可粘贴]\n" + savedCameraText.value);
}

async function copySavedCamera() {
  if (!savedCamera.value) return;
  const text = savedCameraText.value;
  try {
    await navigator.clipboard.writeText(text);
    console.log("[相机视角] 已复制到剪贴板");
  } catch (e) {
    console.warn("[相机视角] 复制失败，请手动选中下方文本", e);
  }
}

function stopAutoTour() {
  autoTour.value = false;
  if (tourTimer) {
    clearInterval(tourTimer);
    tourTimer = null;
  }
}

function toggleAutoTour() {
  if (scriptPlaying.value) return;
  autoTour.value = !autoTour.value;
  if (autoTour.value) {
    tourIndex = TOUR_SPOTS.findIndex((s) => s.key === activeSpot.value);
    if (tourIndex < 0) tourIndex = 0;
    flyToSpot(TOUR_SPOTS[tourIndex]);
    tourTimer = setInterval(() => {
      tourIndex = (tourIndex + 1) % TOUR_SPOTS.length;
      flyToSpot(TOUR_SPOTS[tourIndex]);
    }, 7000);
  } else {
    stopAutoTour();
  }
}

function clearScriptTimers() {
  if (scriptTimer) {
    clearTimeout(scriptTimer);
    scriptTimer = null;
  }
  if (scriptHoldTimer) {
    clearTimeout(scriptHoldTimer);
    scriptHoldTimer = null;
  }
  if (scriptProgressTimer) {
    clearInterval(scriptProgressTimer);
    scriptProgressTimer = null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => {
    scriptTimer = setTimeout(resolve, ms);
  });
}

/** 可暂停 / 可中止的等待 */
async function wait(ms) {
  let left = ms;
  while (left > 0) {
    if (scriptAbort) return;
    if (scriptPaused.value) {
      await sleep(120);
      continue;
    }
    const slice = Math.min(120, left);
    await sleep(slice);
    left -= slice;
  }
}

function stopScript() {
  scriptGen += 1;
  scriptAbort = true;
  clearScriptTimers();
  scriptPlaying.value = false;
  scriptPaused.value = false;
  scriptStepIndex.value = -1;
  scriptCaption.value = "";
  scriptProgress.value = 0;
  scriptAbort = false;
}

function startStepProgress(holdMs) {
  scriptProgress.value = 0;
  if (scriptProgressTimer) clearInterval(scriptProgressTimer);
  let elapsed = 0;
  scriptProgressTimer = setInterval(() => {
    if (scriptPaused.value || scriptAbort) return;
    elapsed += 80;
    scriptProgress.value = Math.min(100, (elapsed / holdMs) * 100);
  }, 80);
}

async function runScriptFrom(startIndex = 0) {
  const gen = ++scriptGen;
  stopAutoTour();
  scriptAbort = false;
  scriptPlaying.value = true;
  scriptPaused.value = false;

  for (let i = startIndex; i < DEMO_SCRIPT.steps.length; i++) {
    if (scriptAbort || gen !== scriptGen) return;

    const step = DEMO_SCRIPT.steps[i];
    scriptStepIndex.value = i;
    scriptCaption.value = step.caption || "";
    applyLayersConfig(step);
    applyEffectsConfig(step);

    if (step.fly) {
      const spot = getSpotByKey(step.fly);
      if (spot) {
        // 先挂标签再飞，保证每幕都有名称
        showSceneLabelForSpot(spot, step.title);
        flyToSpot(spot, undefined, step.title);
      }
      await wait((spot?.duration ?? 2.2) * 1000);
    } else {
      // 无飞行也显示本幕标题（落在当前相机下方）
      const cam = viewerRef?.camera;
      if (cam && sceneLabelRef) {
        const carto = Cesium.Cartographic.fromCartesian(cam.positionWC);
        sceneLabelRef.show(
          step.title,
          Cesium.Math.toDegrees(carto.longitude),
          Cesium.Math.toDegrees(carto.latitude),
          200
        );
      }
    }

    if (scriptAbort || gen !== scriptGen) return;

    const hold = step.hold ?? 4000;
    startStepProgress(hold);
    await wait(hold);
    if (scriptProgressTimer) {
      clearInterval(scriptProgressTimer);
      scriptProgressTimer = null;
    }
    if (gen !== scriptGen) return;
    scriptProgress.value = 100;
  }

  if (gen !== scriptGen) return;

  if (DEMO_SCRIPT.loop) {
    await runScriptFrom(0);
    return;
  }

  scriptPlaying.value = false;
  scriptPaused.value = false;
  scriptCaption.value = "";
  setTimeout(() => {
    if (!scriptPlaying.value && gen === scriptGen) {
      scriptStepIndex.value = -1;
      scriptProgress.value = 0;
    }
  }, 1200);
}

function toggleScript() {
  if (!scriptPlaying.value) {
    runScriptFrom(0);
    return;
  }
  scriptPaused.value = !scriptPaused.value;
}

function jumpToScriptStep(index) {
  if (loading.value) return;
  clearScriptTimers();
  scriptGen += 1;
  scriptAbort = true;
  scriptPlaying.value = false;
  scriptPaused.value = false;
  setTimeout(() => {
    scriptAbort = false;
    runScriptFrom(index);
  }, 40);
}

function bindPick(viewer) {
  pickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  pickHandler.setInputAction((movement) => {
    const pickedObj = viewer.scene.pick(movement.position);
    if (!Cesium.defined(pickedObj) || !pickedObj.id) {
      picked.value = null;
      return;
    }
    const entity = pickedObj.id;
    const info = { 名称: entity.name || "未命名要素" };
    const ds = entity.entityCollection?.owner;
    if (ds?.name) info.图层 = ds.name;
    const props = entity.properties;
    if (props) {
      const keys = props.propertyNames || [];
      for (const key of keys.slice(0, 8)) {
        try {
          const val = props[key]?.getValue?.(viewer.clock.currentTime);
          if (val != null && String(val).trim() !== "") {
            info[key] = String(val);
          }
        } catch (_) {
          /* ignore */
        }
      }
    }
    picked.value = info;
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
}

Cesium.Ion.defaultAccessToken =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIwMDE2ZTg5Mi1mNDJmLTQ5NWItOGMwZC01YzUyZmE4ZDY2NzMiLCJpZCI6MTM1NTcxLCJzdWIiOiJoYW53dWppMzIxIiwiaXNzIjoiaHR0cHM6Ly9hcGkuY2VzaXVtLmNvbSIsImF1ZCI6Inpob3VxbSIsImlhdCI6MTc4NDUxMzI1MX0.7LaUZiXqL_Q_ycOh04LGnk0d62aqI-M0kghlDI61TTk";

// 与 vue.config.js 的 publicPath 保持一致（生产为 /cesium/yc/）
window.CESIUM_BASE_URL = process.env.BASE_URL;

Cesium.Camera.DEFAULT_VIEW_RECTANGLE = Cesium.Rectangle.fromDegrees(
  89.5,
  20.4,
  110.4,
  61.2
);

onMounted(async () => {
  updateClock();
  clockTimer = setInterval(updateClock, 1000);

  const tBoot = performance.now();
  setLoad(10, "创建三维场景…");

  const viewer = new Cesium.Viewer("cesiumContainer", {
    infoBox: false,
    animation: false,
    timeline: false,
    terrain: Cesium.Terrain.fromWorldTerrain(),
    shouldAnimate: true,
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    navigationHelpButton: false,
    fullscreenButton: false,
  });
  viewerRef = viewer;
  viewer.cesiumWidget.creditContainer.style.display = "none";

  viewer.scene.globe.enableLighting = true;
  viewer.scene.skyBox.show = false;
  viewer.scene.sun.show = false;
  viewer.scene.moon.show = false;
  viewer.scene.skyAtmosphere.show = false;
  viewer.scene.backgroundColor = Cesium.Color.BLACK;
  viewer.scene.globe.baseColor = Cesium.Color.BLACK;
  viewer.scene.postProcessStages.fxaa.enabled = true;
  modifyMap(viewer);

  const controller = viewer.scene.screenSpaceCameraController;
  controller.enableRotate = true;
  controller.enableTilt = true;
  controller.enableTranslate = true;
  controller.enableZoom = true;

  const applyCam = (cam, ground) => {
    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(
        cam.lng,
        cam.lat,
        ground + cam.height
      ),
      orientation: {
        heading: Cesium.Math.toRadians(cam.heading ?? 0),
        pitch: Cesium.Math.toRadians(cam.pitch ?? -30),
        roll: 0,
      },
    });
  };

  applyCam(CAMERA_DEFAULT, 1110);

  setLoad(25, "采样地表高度…");
  const groundHeight = await sampleGroundHeight(
    viewer,
    CENTER.lng,
    CENTER.lat
  );
  groundHeightRef = groundHeight;
  console.log("[yc] 地表高度:", groundHeight);

  applyCam(CAMERA_DEFAULT, groundHeight);

  setLoad(45, "加载道路与建筑…");
  const primary = await loadBaseLayers(viewer, ["roads", "buildings"]);
  layers.value = primary;

  setLoad(70, "挂载视觉特效…");
  const lightCone = new LightCone(viewer, { groundHeight });
  // 核心区：青色光锥 + 橙色光墙（落在核心区注视点）
  const lightConeCore = new LightCone(viewer, {
    groundHeight,
    lng: FOCUS_CORE.lng,
    lat: FOCUS_CORE.lat,
    color: Cesium.Color.fromCssColorString("#18ffff"),
    scale: 280,
    heightBase: groundHeight + 160,
  });
  const rectFlyLight = new RectFlyLight(viewer, { groundHeight, count: 60 });
  const roadLightLine = new RoadLightLine(viewer, {
    groundHeight,
    maxCount: 300,
  });
  const radarLight = new RadarLight(viewer, { groundHeight });
  const lightSpread = new LightSpread(viewer, { groundHeight });
  const lightWall = new LightWall(viewer, {
    groundHeight,
    color: Cesium.Color.fromCssColorString("#ea80fc"),
    materialName: "LightWallMaterial_purple",
  });
  const lightWallCore = new LightWall(viewer, {
    groundHeight,
    lng: FOCUS_CORE.lng,
    lat: FOCUS_CORE.lat,
    halfSize: 0.005,
    wallHeight: 240,
    color: Cesium.Color.fromCssColorString("#ff9100"),
    materialName: "LightWallMaterial_orange",
    name: "coreLightWall",
  });
  const particleRed = new ParticleLight(viewer, Cesium.Color.RED, {
    groundHeight,
  });

  sceneLabelRef = new SceneLabel(viewer, { groundHeight, lift: 120 });
  alertLayerRef = new AlertLayer(viewer, {
    groundHeight,
    alerts: DEMO_ALERTS,
  });
  alertLayerRef.setVisible(alertVisible.value);

  const coneGroup = {
    setVisible(show) {
      lightCone.setVisible(show);
      lightConeCore.setVisible(show);
    },
  };
  const wallGroup = {
    setVisible(show) {
      lightWall.setVisible(show);
      lightWallCore.setVisible(show);
    },
  };

  effects.value = [
    { key: "cone", name: "光锥", colorHex: "#ffeb3b", visible: true, instance: coneGroup },
    { key: "fly", name: "上升飞线", colorHex: "#b39ddb", visible: true, instance: rectFlyLight },
    { key: "roadLine", name: "道路飞线", colorHex: "#69f0ae", visible: true, instance: roadLightLine },
    { key: "radar", name: "雷达扫描", colorHex: "#40c4ff", visible: true, instance: radarLight },
    { key: "spread", name: "六边扩散", colorHex: "#18ffff", visible: true, instance: lightSpread },
    { key: "wall", name: "光墙", colorHex: "#ea80fc", visible: true, instance: wallGroup },
    { key: "particle", name: "粒子特效", colorHex: "#ff5252", visible: true, instance: particleRed },
  ];

  bindPick(viewer);

  setLoad(85, "加载水域与铁路…");
  loadBaseLayers(viewer, ["landuse", "water", "waterway", "railway"]).then(
    (rest) => {
      layers.value = [...layers.value, ...rest];
      console.log(`[yc] 次要图层 ${(performance.now() - tBoot).toFixed(0)}ms`);
    }
  );

  setLoad(100, "就绪");
  console.log(`[yc] 启动完成 ${(performance.now() - tBoot).toFixed(0)}ms`);
  setTimeout(() => {
    loading.value = false;
  }, 400);
});

onUnmounted(() => {
  if (clockTimer) clearInterval(clockTimer);
  if (tourTimer) clearInterval(tourTimer);
  stopScript();
  pickHandler?.destroy?.();
  alertLayerRef?.destroy?.();
  alertLayerRef = null;
  viewerRef?.destroy?.();
});
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&family=Noto+Sans+SC:wght@400;500;700&display=swap");

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  --cyan: #3de7ff;
  --cyan-dim: rgba(61, 231, 255, 0.35);
  --panel: rgba(6, 18, 32, 0.78);
  --line: rgba(61, 231, 255, 0.22);
  --text: #d8f6ff;
  --muted: rgba(216, 246, 255, 0.55);
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: "Noto Sans SC", "PingFang SC", sans-serif;
  color: var(--text);
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(20, 60, 90, 0.45), transparent 60%),
    #02060c;
}

#cesiumContainer {
  width: 100%;
  height: 100%;
}

/* —— 顶栏 —— */
.top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  height: 72px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 28px;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(2, 10, 20, 0.92) 0%,
    rgba(2, 10, 20, 0.55) 70%,
    transparent 100%
  );
}

.top-bar__brand {
  text-align: center;
  pointer-events: none;
}

.top-bar__brand h1 {
  font-family: "Orbitron", "Noto Sans SC", sans-serif;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: #fff;
  text-shadow: 0 0 24px rgba(61, 231, 255, 0.45);
}

.top-bar__brand p {
  margin-top: 2px;
  font-size: 12px;
  letter-spacing: 0.35em;
  color: var(--cyan);
  opacity: 0.85;
}

.top-bar__side {
  display: flex;
  align-items: center;
}

.top-bar__side--right {
  justify-content: flex-end;
}

.top-bar__org,
.top-bar__clock {
  font-family: "Orbitron", monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: var(--muted);
}

.top-bar__clock {
  color: var(--cyan);
  font-size: 13px;
}

/* —— 加载 —— */
.loader {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  background: rgba(2, 6, 12, 0.72);
  backdrop-filter: blur(6px);
}

.loader__card {
  width: min(360px, 80vw);
  padding: 28px 32px;
  border: 1px solid var(--line);
  background: var(--panel);
}

.loader__title {
  font-family: "Orbitron", sans-serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  margin-bottom: 16px;
  color: var(--cyan);
}

.loader__bar {
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.loader__bar i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0aa, var(--cyan));
  transition: width 0.35s ease;
}

.loader__msg {
  margin-top: 12px;
  font-size: 12px;
  color: var(--muted);
}

/* —— 左右面板：恢复可读字号，间距略收 —— */
.left-panel,
.layer-panel {
  position: absolute;
  top: 84px;
  bottom: 52px;
  z-index: 15;
  width: 280px;
  padding: 12px 14px;
  background: var(--panel);
  border: 1px solid var(--line);
  backdrop-filter: blur(12px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.left-panel {
  left: 18px;
}

.layer-panel {
  right: 18px;
}

.panel-block--kpi {
  flex-shrink: 0;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.left-panel > .panel-block {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.left-panel > .panel-block + .panel-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.left-panel > .panel-block--pick {
  flex: 0 0 auto;
  max-height: 110px;
}

.left-panel > .panel-block--alerts {
  flex: 1.15;
}

.panel-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.panel-block__head h3,
.layer-panel__header h3 {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.12em;
  color: var(--cyan);
}

.kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.kpi {
  padding: 8px 10px;
  border: 1px solid rgba(61, 231, 255, 0.12);
  background: rgba(61, 231, 255, 0.04);
}

.kpi__label {
  font-size: 11px;
  color: var(--muted);
}

.kpi__value {
  margin-top: 2px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.kpi__value em {
  font-family: "Orbitron", monospace;
  font-style: normal;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.kpi__value span {
  font-size: 11px;
  color: var(--cyan);
}

.tour-btn {
  border: 1px solid var(--cyan-dim);
  background: transparent;
  color: var(--cyan);
  font-size: 11px;
  padding: 4px 10px;
  cursor: pointer;
  letter-spacing: 0.06em;
  transition: 0.2s;
  font-family: inherit;
}

.tour-btn:hover,
.tour-btn--on {
  background: rgba(61, 231, 255, 0.15);
}

.tour-btn--ghost {
  border-color: rgba(255, 255, 255, 0.15);
  color: var(--muted);
}

.script-meta {
  font-family: "Orbitron", monospace;
  font-size: 11px;
  color: var(--muted);
}

.script-controls {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.script-progress {
  margin-bottom: 8px;
  flex-shrink: 0;
}

.script-progress__bar {
  height: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.script-progress__bar i {
  display: block;
  height: 100%;
  background: var(--cyan);
  transition: width 0.08s linear;
}

.script-progress__text {
  margin-top: 4px;
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.script-steps {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.script-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  cursor: pointer;
  border-left: 2px solid transparent;
  transition: 0.15s;
}

.script-step:hover {
  background: rgba(61, 231, 255, 0.06);
}

.script-step--active {
  border-left-color: var(--cyan);
  background: rgba(61, 231, 255, 0.12);
}

.script-step--done .script-step__title {
  color: var(--muted);
}

.script-step__idx {
  font-family: "Orbitron", monospace;
  font-size: 10px;
  color: var(--cyan);
  width: 16px;
}

.script-step__title {
  font-size: 12px;
  color: #fff;
}

.tour-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(61, 231, 255, 0.45) rgba(255, 255, 255, 0.06);
}

.tour-list::-webkit-scrollbar {
  width: 4px;
}

.tour-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}

.tour-list::-webkit-scrollbar-thumb {
  background: rgba(61, 231, 255, 0.4);
  border-radius: 2px;
}

.tour-list::-webkit-scrollbar-thumb:hover {
  background: rgba(61, 231, 255, 0.65);
}

.tour-item {
  padding: 7px 10px;
  border: 1px solid transparent;
  border-left: 2px solid transparent;
  cursor: pointer;
  transition: 0.2s;
  background: rgba(255, 255, 255, 0.02);
}

.tour-item:hover {
  border-color: rgba(61, 231, 255, 0.15);
  background: rgba(61, 231, 255, 0.06);
}

.tour-item--active {
  border-left-color: var(--cyan);
  background: rgba(61, 231, 255, 0.1);
}

.tour-item__name {
  display: block;
  font-size: 13px;
  color: #fff;
  line-height: 1.35;
}

.cam-save {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-shrink: 0;
}

.cam-save__out {
  margin-top: 6px;
  padding: 8px;
  font-family: "Orbitron", ui-monospace, monospace;
  font-size: 10px;
  line-height: 1.45;
  color: var(--cyan);
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(61, 231, 255, 0.15);
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 90px;
  overflow: auto;
  flex-shrink: 0;
}

.alert-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.alert-stat {
  padding: 6px 4px;
  text-align: center;
  border: 1px solid rgba(61, 231, 255, 0.12);
  background: rgba(61, 231, 255, 0.04);
}

.alert-stat em {
  display: block;
  font-family: "Orbitron", monospace;
  font-style: normal;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.alert-stat span {
  font-size: 10px;
  color: var(--muted);
}

.alert-stat--critical em {
  color: #ff5252;
}

.alert-stat--warning em {
  color: #ffb300;
}

.alert-stat--info em {
  color: #40c4ff;
}

.alert-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(61, 231, 255, 0.45) rgba(255, 255, 255, 0.06);
}

.alert-list::-webkit-scrollbar {
  width: 4px;
}

.alert-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}

.alert-list::-webkit-scrollbar-thumb {
  background: rgba(61, 231, 255, 0.4);
  border-radius: 2px;
}

.alert-list::-webkit-scrollbar-thumb:hover {
  background: rgba(61, 231, 255, 0.65);
}

.alert-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-left: 2px solid transparent;
  background: rgba(255, 255, 255, 0.02);
  transition: 0.15s;
}

.alert-item:hover {
  background: rgba(61, 231, 255, 0.06);
}

.alert-item--active {
  background: rgba(61, 231, 255, 0.1);
}

.alert-item--critical {
  border-left-color: #ff5252;
}

.alert-item--warning {
  border-left-color: #ffb300;
}

.alert-item--info {
  border-left-color: #40c4ff;
}

.alert-item__level {
  flex-shrink: 0;
  font-size: 10px;
  letter-spacing: 0.06em;
  padding: 2px 5px;
  margin-top: 1px;
  border: 1px solid currentColor;
  opacity: 0.9;
}

.alert-item--critical .alert-item__level {
  color: #ff5252;
}

.alert-item--warning .alert-item__level {
  color: #ffb300;
}

.alert-item--info .alert-item__level {
  color: #40c4ff;
}

.alert-item__body {
  min-width: 0;
  flex: 1;
}

.alert-item__title {
  font-size: 12px;
  color: #fff;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-item__meta {
  margin-top: 2px;
  font-size: 10px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 地图告警 HTML 标签（比 Cesium Label 更清晰） */
.alert-overlay-root {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 8;
}

.alert-map-label {
  position: absolute;
  left: 0;
  top: 0;
  padding: 3px 8px;
  font-family: "Noto Sans SC", "Microsoft YaHei", sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.02em;
  color: #fff;
  white-space: nowrap;
  background: rgba(4, 20, 30, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
  will-change: transform;
  -webkit-font-smoothing: antialiased;
}

.alert-map-label--critical {
  border-color: rgba(255, 82, 82, 0.65);
  color: #ffc9c9;
}

.alert-map-label--warning {
  border-color: rgba(255, 179, 0, 0.65);
  color: #ffe29a;
}

.alert-map-label--info {
  border-color: rgba(64, 196, 255, 0.65);
  color: #b8ecff;
}

.panel-block--pick {
  overflow: hidden;
}

.pick-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pick-info__row {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 8px;
  font-size: 12px;
}

.pick-info__row dt {
  color: var(--muted);
}

.pick-info__row dd {
  color: #fff;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.layer-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.layer-panel__header--sub {
  margin-top: 10px;
}

.layer-panel__count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: rgba(61, 231, 255, 0.15);
  color: var(--cyan);
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  font-family: "Orbitron", monospace;
}

.layer-panel__list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex-shrink: 0;
}

.layer-panel__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 2px;
}

.layer-panel__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.layer-panel__name {
  flex: 1;
  font-size: 13px;
  line-height: 1.3;
}

.layer-panel__empty {
  font-size: 13px;
  color: var(--muted);
  padding: 6px 0;
}

.switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch__slider {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.14);
  transition: 0.2s;
}

.switch__slider::before {
  content: "";
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  top: 3px;
  border-radius: 50%;
  background: #fff;
  transition: 0.2s;
}

.switch input:checked + .switch__slider {
  background: var(--cyan);
}

.switch input:checked + .switch__slider::before {
  transform: translateX(18px);
}

/* —— 底部堆叠：字幕 + 图例 —— */
.bottom-stack {
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  z-index: 18;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: calc(100vw - 340px);
  pointer-events: none;
}

.caption {
  min-width: 280px;
  max-width: 100%;
  padding: 10px 22px 12px;
  text-align: center;
  pointer-events: none;
  background: rgba(4, 16, 28, 0.88);
  border: 1px solid rgba(61, 231, 255, 0.25);
  animation: captionIn 0.45s ease;
}

.caption__eyebrow {
  font-family: "Orbitron", monospace;
  font-size: 9px;
  letter-spacing: 0.28em;
  color: var(--cyan);
  opacity: 0.75;
}

.caption__title {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: #fff;
}

.caption__text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--muted);
}

@keyframes captionIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* —— 底栏图例 —— */
.legend-bar {
  position: static;
  left: auto;
  bottom: auto;
  transform: none;
  z-index: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 18px;
  background: var(--panel);
  border: 1px solid var(--line);
  backdrop-filter: blur(10px);
  white-space: nowrap;
  max-width: 100%;
  overflow-x: auto;
  pointer-events: auto;
}

.legend-bar__label {
  font-size: 11px;
  letter-spacing: 0.2em;
  color: var(--cyan);
  font-family: "Orbitron", sans-serif;
}

.legend-bar__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text);
  opacity: 0.9;
}

.legend-bar__item--off {
  opacity: 0.35;
}

.legend-bar__item i {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}

@media (max-width: 1100px) {
  .left-panel,
  .layer-panel {
    width: 240px;
  }
  .top-bar__brand h1 {
    font-size: 20px;
    letter-spacing: 0.12em;
  }
}

@media (max-width: 800px) {
  .left-panel {
    display: none;
  }
  .bottom-stack {
    left: 16px;
    right: 16px;
    transform: none;
    max-width: none;
  }
}
</style>
